import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { groupAPI, billAPI, memberAPI, formatApiError } from '../services/api';
import { ArrowLeftIcon, PlusIcon, UserPlusIcon, ShareIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { ShoppingBagIcon, ShoppingCartIcon, TruckIcon, PhoneIcon, FilmIcon, WifiIcon, GiftIcon } from '@heroicons/react/24/outline';
import AddBillModal from '../components/AddBillModal';
import AddMemberModal from '../components/AddMemberModal';
import GroupMembersModal from '../components/GroupMembersModal';
import TransactionDetailModal from '../components/TransactionDetailModal';
import EditBillModal from '../components/EditBillModal';
import ShareModal from '../components/ShareModal';
import { toast } from 'sonner';
import { formatTimeAgo } from '../utils/dateUtils';

const GroupDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('transactions');
  const [showAddBill, setShowAddBill] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [editingTx, setEditingTx] = useState(null);
  const [shareData, setShareData] = useState(null); // null or { settlement } or {}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGroupDetails();
  }, [id]);

  const fetchGroupDetails = async () => {
    try {
      const response = await groupAPI.getGroupDetails(id);
      setGroup(response.data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleAddBill = async (billData) => {
    try {
      await billAPI.createBill({ ...billData, group_id: parseInt(id) });
      toast.success('Tagihan berhasil ditambahkan!');
      fetchGroupDetails();
      setShowAddBill(false);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const handleUpdateBill = async (billData) => {
    try {
      await billAPI.updateBill(billData);
      toast.success('Tagihan berhasil diperbarui!');
      fetchGroupDetails();
      setEditingTx(null);
      setSelectedTx(null);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const handleDeleteBill = async (billId) => {
    try {
      await billAPI.deleteBill(billId);
      toast.success('Tagihan berhasil dihapus!');
      fetchGroupDetails();
      setEditingTx(null);
      setSelectedTx(null);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const handleSettleUp = async (settlement) => {
    try {
      await billAPI.settleBill({
        group_id: parseInt(id),
        from_member_id: settlement.fromMemberId,
        to_member_id: settlement.toMemberId,
        amount: settlement.amount
      });
      toast.success('Berhasil di-settle!');
      fetchGroupDetails();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const handleAddMember = async (memberData) => {
    try {
      await memberAPI.addMember({ group_id: parseInt(id), ...memberData });
      toast.success('Member berhasil ditambahkan!');
      setShowAddMember(false);
      fetchGroupDetails();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const getCategoryIcon = (category) => {
    const icons = { food: ShoppingBagIcon, transport: TruckIcon, groceries: ShoppingCartIcon, entertainment: FilmIcon, internet: WifiIcon, other: GiftIcon };
    return icons[category] || GiftIcon;
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);



  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
        <p className="text-slate-600 mb-4">Grup tidak ditemukan atau Anda bukan member.</p>
        <button onClick={() => navigate('/dashboard')} className="text-primary font-semibold">Kembali ke Dashboard</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="bg-surface px-6 pt-12 pb-8 rounded-b-[32px] mb-6">
        <button onClick={() => navigate('/dashboard')} data-testid="back-btn" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeftIcon className="w-5 h-5" /><span className="font-medium">Kembali</span>
        </button>
        <h1 className="text-2xl font-bold text-slate-900 mb-6">{group.name}</h1>

        {/* Member Bar */}
        <div className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide items-center" data-testid="member-bar">
          <button onClick={() => setShowAddMember(true)} data-testid="add-member-btn"
            className="w-14 h-14 rounded-full border-2 border-dashed border-primary flex items-center justify-center flex-shrink-0 hover:bg-primary/5 transition-colors">
            <UserPlusIcon className="w-6 h-6 text-primary" />
          </button>
          {group.members.slice(0, 3).map((member) => (
            <div key={member.id} className="flex flex-col items-center gap-1 flex-shrink-0">
              <div className="relative">
                <img src={`https://api.dicebear.com/9.x/micah/svg?seed=${member.name}`} alt={member.name} className="w-14 h-14 rounded-full border-2 border-white shadow-sm bg-surface/50" />
                {member.user_id && (
                  <div className="absolute -bottom-1 -right-1 bg-white p-0.5 rounded-full shadow-sm" title="Terdaftar">
                    <CheckCircleIcon className="w-4 h-4 text-primary fill-primary/10" />
                  </div>
                )}
              </div>
              <span className="text-xs text-slate-600 font-medium">{member.name}</span>
            </div>
          ))}
          {group.members.length > 3 && (
            <button 
              onClick={() => setShowMembers(true)}
              className="flex flex-col items-center gap-1 flex-shrink-0 group"
            >
              <div className="w-14 h-14 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center shadow-sm group-hover:bg-slate-200 transition-colors">
                <span className="text-sm font-bold text-slate-600">+{group.members.length - 3}</span>
              </div>
              <span className="text-xs text-slate-500 font-medium whitespace-nowrap">Lihat Semua</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 mb-6">
        <div className="flex gap-2 bg-surface p-1 rounded-full">
          <button onClick={() => setActiveTab('transactions')} data-testid="transactions-tab"
            className={`flex-1 py-2 px-4 rounded-full font-semibold text-sm transition-all ${activeTab === 'transactions' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
            Transaksi
          </button>
          <button onClick={() => setActiveTab('settlement')} data-testid="settlement-tab"
            className={`flex-1 py-2 px-4 rounded-full font-semibold text-sm transition-all ${activeTab === 'settlement' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
            Settlement
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6">
        {activeTab === 'transactions' ? (
          <div className="relative pl-8 border-l-2 border-border ml-4 space-y-8" data-testid="transaction-timeline">
            {group.transactions && group.transactions.length > 0 ? (
              group.transactions.map((transaction, idx) => {
                const Icon = getCategoryIcon(transaction.category);
                return (
                  <motion.div key={transaction.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.1 }}
                    className="relative cursor-pointer" data-testid={`transaction-${transaction.id}`}
                    onClick={() => setSelectedTx(transaction)}>
                    <div className="absolute -left-[41px] bg-white border-2 border-border w-12 h-12 rounded-full flex items-center justify-center shadow-sm">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="bg-white border border-border rounded-3xl p-4 hover:shadow-soft hover:border-primary/30 transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-slate-900">{transaction.description}</h4>
                          <p className="text-xs text-slate-500">{formatTimeAgo(transaction.date)}</p>
                        </div>
                        <p className="text-lg font-bold text-slate-900">{formatCurrency(transaction.amount)}</p>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-600">Dibayar oleh:</span>
                        <span className="font-semibold text-primary">{transaction.paidBy}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm mt-1">
                        <span className="text-slate-600">Split:</span>
                        <span className="text-slate-900">{transaction.splitBetween.join(', ')}</span>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <p className="text-center text-slate-500 py-8 -ml-8">Belum ada transaksi</p>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="settlement-list">
            {group.settlements && group.settlements.length > 0 ? (
              group.settlements.map((settlement, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
                  className="bg-white border border-border rounded-3xl p-5" data-testid={`settlement-${idx}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <img src={`https://api.dicebear.com/9.x/micah/svg?seed=${settlement.from}`} alt={settlement.from} className="w-10 h-10 rounded-full" />
                      <div>
                        <p className="font-semibold text-slate-900">{settlement.from}</p>
                        <p className="text-xs text-slate-500">harus bayar ke</p>
                      </div>
                    </div>
                    <div className="text-center px-4">
                      <ArrowLeftIcon className="w-5 h-5 text-slate-400 rotate-180" />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">{settlement.to}</p>
                        <p className="text-xs text-slate-500">penerima</p>
                      </div>
                      <img src={`https://api.dicebear.com/9.x/micah/svg?seed=${settlement.to}`} alt={settlement.to} className="w-10 h-10 rounded-full" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <p className="text-2xl font-bold text-danger">{formatCurrency(settlement.amount)}</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShareData({ settlement })}
                        data-testid={`share-btn-${idx}`}
                        className="w-10 h-10 rounded-full bg-surface flex items-center justify-center hover:bg-surface-hover transition-colors"
                      >
                        <ShareIcon className="w-5 h-5 text-slate-600" />
                      </button>
                      <button onClick={() => handleSettleUp(settlement)} data-testid={`settle-btn-${idx}`}
                        className="bg-success text-white px-5 py-2 rounded-full font-semibold hover:bg-success/90 active:scale-95 transition-all text-sm">
                        Settle Up
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500">Semua sudah lunas!</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Bill FAB */}
      <motion.button initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}
        onClick={() => setShowAddBill(true)} data-testid="add-bill-fab"
        className="fixed bottom-20 right-6 w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center shadow-float hover:scale-105 active:scale-95 transition-transform z-50">
        <PlusIcon className="w-8 h-8" />
      </motion.button>

      {/* Modals */}
      {showAddBill && <AddBillModal members={group.members} onClose={() => setShowAddBill(false)} onSubmit={handleAddBill} />}
      {showAddMember && <AddMemberModal groupId={parseInt(id)} onClose={() => setShowAddMember(false)} onSubmit={handleAddMember} />}
      {showMembers && <GroupMembersModal members={group.members} onClose={() => setShowMembers(false)} />}

      {selectedTx && !editingTx && (
        <TransactionDetailModal
          transaction={selectedTx}
          onClose={() => setSelectedTx(null)}
          onEdit={() => setEditingTx(selectedTx)}
          onDelete={handleDeleteBill}
        />
      )}

      {editingTx && (
        <EditBillModal
          transaction={editingTx}
          members={group.members}
          onClose={() => { setEditingTx(null); setSelectedTx(null); }}
          onSubmit={handleUpdateBill}
          onDelete={handleDeleteBill}
        />
      )}

      {shareData && (
        <ShareModal
          groupId={parseInt(id)}
          groupName={group.name}
          settlement={shareData.settlement}
          onClose={() => setShareData(null)}
        />
      )}
    </div>
  );
};

export default GroupDetail;
